import { useEffect, useState } from 'react'
import { useIsClient, useLocalStorage } from 'usehooks-ts'
import type { TypeComponentLink, TypeHeaderBanner } from '@/types/ctf'
import RichText from '@/components/ui/rich-text'
export type BannerProps = TypeHeaderBanner<'WITHOUT_UNRESOLVABLE_LINKS'>['fields'] & {
  id: string
}

export function Banner({ id, content }: BannerProps) {
  const [isBannerClosed, setIsBannerClosed] = useLocalStorage(`bw-banner-closed-${id}`, typeof window === 'undefined')
  return (
    !isBannerClosed && (
      <div className="z-3 relative bg-bw-deep-blue text-white text-center align-middle pt-2 pb-3 not-last-of-type:border-b border-deepBlue">
        <div className="tracking-wide leading-4 font-medium ml-5 mr-8 lg:mx-12 flex items-center justify-center [&_p]:text-smaller lg:[&_p]:text-base [&_p]:mb-0 text-left lg:text-center [&_a]:text-accent-teal! [&_a]:no-underline hover:[&_a]:underline [&_a]:mx-1">
          <RichText body={content} />
        </div>
        <button
          type="button"
          className="cursor-pointer rounded-sm absolute top-1 right-1 w-6 h-6 md:top-1/2 md:-translate-y-1/2 md:right-4 md:w-8 md:h-8 hover:text-secondary-200 hover:bg-secondary-800"
          onClick={() => {
            setIsBannerClosed(true)
          }}
        >
          <span className="font-bwi text-sm lg:text-base" data-name="bwi-close">
            &#xe93d;
          </span>
        </button>
      </div>
    )
  )
}

export type HeaderBannerProps = {
  isHelpSite?: boolean
  banners?: TypeHeaderBanner<'WITHOUT_UNRESOLVABLE_LINKS'>[]
}

export default function HeaderBanner({ banners, isHelpSite = false }: HeaderBannerProps) {
  const isClient = useIsClient()
  const [path, setPath] = useState('')

  useEffect(() => {
    const { pathname } = window.location
    if (pathname) {
      !pathname.endsWith('/') ? setPath(pathname + '/') : setPath(pathname)
    }
  }, [])

  const testUrl = (page: TypeComponentLink<'WITHOUT_UNRESOLVABLE_LINKS'> | undefined) => {
    if (!page) return null
    let newUrl = new URL(page?.fields?.url || '', 'https://bitwarden.com').pathname
    if (!newUrl.endsWith('/')) {
      newUrl += '/'
    }
    return newUrl
  }

  const bannersNotToShow =
    banners?.filter(({ fields: { helpSite, pagesNotToDisplayOn = [] } }) => {
      if (helpSite === 'Only show on Help Site' && !isHelpSite) {
        return true
      }
      if (helpSite === 'Hide on Help Site' && isHelpSite) {
        return true
      }
      return Array.isArray(pagesNotToDisplayOn) && pagesNotToDisplayOn.some((page) => path === testUrl(page))
    }) || []

  const bannersShow =
    banners?.filter(({ fields: { helpSite, pagesToDisplay = [] } }) => {
      if (helpSite === 'Only show on Help Site' && !isHelpSite) {
        return false
      }
      if (helpSite === 'Hide on Help Site' && isHelpSite) {
        return false
      }
      return Array.isArray(pagesToDisplay) && pagesToDisplay.some((page) => path === testUrl(page))
    }) || []

  const bannersEveryPage =
    banners?.filter((banner) => {
      if (
        ((banner.fields.helpSite === 'Hide on Help Site' && !isHelpSite) ||
          (banner.fields.helpSite === 'Only show on Help Site' && isHelpSite)) &&
        !banner.fields.pagesToDisplay
      ) {
        return banner
      }
    }) || []

  const showBanners = [...bannersShow, ...bannersEveryPage]

  // create a new array from showBanners and remove any banners that are in the bannersNotToShow
  bannersNotToShow.map((dontShowBanner) => {
    showBanners.map((show, index) => {
      if (dontShowBanner.sys.id === show.sys.id) {
        showBanners.splice(index, 1)
      }
    })
  }) || []

  return isClient && showBanners?.map((banner) => <Banner id={banner.sys.id} key={banner.sys.id} {...banner.fields} />)
}
